import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import request from "../../utilities/requests";
import { toast } from "../../utilities/SnackBarUtils";

const SystemMessageDialog = ({ open, flags, onClose }) => {
  const [termsLoaded, setTermsLoaded] = useState(false);
  const [messagesLoaded, setMessagesLoaded] = useState(false);
  const [terms, setTerms] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState(null);
  const [type, setType] = useState(null);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    if (open && flags?.requireTermsAck) {
      getTerms();
    } else {
      setTermsLoaded(true);
    }
    if (open && flags?.requireMessageAck) {
      getUnacknowledged();
    } else {
      setMessagesLoaded(true);
    }
  }, [flags]);

  useEffect(() => {
    if (!termsLoaded || !messagesLoaded) return;

    if (terms) {
      setType("terms");
      setAgreedToTerms(false);
    } else if (message) {
      setType("message");
    } else {
      setType(null);
      onClose();
    }
  }, [termsLoaded, messagesLoaded, terms, message]);

  const getTerms = async () => {
    const result = await request("systemmessage/terms");
    if (result.success) {
      setTerms(result.data);
    }
    setTermsLoaded(true);
  };

  const getUnacknowledged = async () => {
    const result = await request("systemmessage/unacknowledged");
    if (result.success) {
      setMessages(result.data);
      setMessage(result.data[0]);
    }
    setMessagesLoaded(true);
  };

  const handleAck = async () => {
    let url = "systemmessage/acknowledge/";
    if (type === "terms") {
      url = url + `terms/${terms._id}`;
    } else {
      url = url + message._id;
    }
    const result = await request(url, "post", {});
    if (!result.success) {
      toast.error(result.error);
      return;
    }
    if (type === "terms") {
      setTerms(null);
      localStorage.removeItem("requireTermsAck");
    } else {
      const msgs = [...messages];
      msgs.shift();
      if (msgs.length > 0) {
        setMessages([...msgs]);
        setMessage(msgs[0]);
      } else {
        setMessage(null);
        localStorage.removeItem("requireMessageAck");
      }
    }
  };

  const renderContent = () => {
    if (!termsLoaded || !messagesLoaded) {
      return (
        <DialogContent
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20
          }}
        >
          <CircularProgress />
        </DialogContent>
      );
    }
    if (type === "terms") {
      return (
        <DialogContent>
          {terms?.htmlBody && (
            <div
              dangerouslySetInnerHTML={{ __html: terms.htmlBody }}
              style={{ paddingTop: 8 }}
            />
          )}
        </DialogContent>
      );
    } else if (type === "message") {
      return (
        <DialogContent>
          {message?.htmlBody && (
            <div
              dangerouslySetInnerHTML={{ __html: message.htmlBody }}
              style={{ paddingTop: 8 }}
            />
          )}
        </DialogContent>
      );
    } else {
      return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(e, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      fullWidth
      disableEscapeKeyDown
    >
      <DialogTitle>{type === "message" ? message?.title : ""}</DialogTitle>
      {renderContent()}
      <DialogActions sx={{ flexDirection: "column", alignItems: "flex-end", padding: '20px', gap: '10px' }}>
        {type === "terms" && (
          <div style={{ paddingLeft: 16, paddingBottom: 8 }}>
            <label>
              <input
                type="checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              &nbsp;I have read and agree to the terms and conditions above
            </label>
          </div>
        )}
        <Button
          onClick={handleAck}
          disabled={type === "terms" && !agreedToTerms}
          variant="outlined"
        >
          {type === "terms" ? "I AGREE" : "OK"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SystemMessageDialog;
